import React, { useEffect, useState } from 'react';
import { Image } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import { getColeccionItemImageUrl } from 'api/coleccionItemImage';

// Thumbnail lazy-loaded para tablas/listas/autocomplete de items. Resuelve
// el presigned GET al montarse. Cache module-level por (itemId, imageKey)
// para no re-fetchear la misma URL dentro de su ventana de vigencia — util
// cuando el mismo item aparece multiple veces (ej. search del BudgetEditor).

// Presigned GET dura 1h; refresh a los 55 min para cubrir drift de reloj.
const CACHE_TTL_MS = 55 * 60 * 1000;
const urlCache = new Map(); // `${itemId}:${imageKey}` -> { url, fetchedAt }

const getCachedUrl = (itemId, imageKey) => {
  const entry = urlCache.get(`${itemId}:${imageKey}`);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
    urlCache.delete(`${itemId}:${imageKey}`);
    return null;
  }
  return entry.url;
};

const setCachedUrl = (itemId, imageKey, url) => {
  urlCache.set(`${itemId}:${imageKey}`, { url, fetchedAt: Date.now() });
};

function ColeccionItemImageThumb({ itemId, imageKey, size = 40 }) {
  const [url, setUrl] = useState(() => getCachedUrl(itemId, imageKey));

  useEffect(() => {
    if (!itemId || !imageKey) {
      setUrl(null);
      return undefined;
    }
    const cached = getCachedUrl(itemId, imageKey);
    if (cached) {
      setUrl(cached);
      return undefined;
    }
    let canceled = false;
    getColeccionItemImageUrl(itemId)
      .then((resolved) => {
        setCachedUrl(itemId, imageKey, resolved);
        if (!canceled) setUrl(resolved);
      })
      .catch(() => {
        if (!canceled) setUrl(null);
      });
    return () => {
      canceled = true;
    };
  }, [itemId, imageKey]);

  if (!imageKey) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 4,
          background: '#f5f5f5',
          color: '#bfbfbf',
        }}
      >
        <PictureOutlined />
      </div>
    );
  }

  if (!url) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: 4,
          background: '#f5f5f5',
        }}
      />
    );
  }

  return (
    <Image
      src={url}
      width={size}
      height={size}
      style={{ objectFit: 'cover', borderRadius: 4 }}
      preview={{ src: url }}
    />
  );
}

export default ColeccionItemImageThumb;
